// rsaPathUtils.js — path/geometry utility functions shared by RSAModelAnalysis and RSAExistingAnalysis

import { catmullRomSpline, SPLINE_SAMPLES_EDIT } from '../PathSelectorMap/splineUtils';

export function getWaypointCoordinateIndices(pathData, waypoints) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length) return [];
  const coords = pathData.geometry.coordinates;
  const indices = [];
  for (const wp of waypoints) {
    const lat = wp.lat;
    const lng = wp.lng;
    let bestIdx = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < coords.length; i++) {
      const c = coords[i];
      const clat = Array.isArray(c) ? c[1] : c.lat;
      const clng = Array.isArray(c) ? c[0] : c.lng;
      const d2 = (clat - lat) ** 2 + (clng - lng) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestIdx = i;
      }
    }
    indices.push(bestIdx);
  }
  return indices;
}

/** Slice pathData to the segment between waypoint startIdx and endIdx (inclusive) */
export function slicePathDataByWaypointRange(pathData, waypoints, startIdx, endIdx) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length) return null;
  const coords = pathData.geometry.coordinates;
  const wpIndices = getWaypointCoordinateIndices(pathData, waypoints);
  const i0 = Math.min(wpIndices[startIdx] ?? 0, wpIndices[endIdx] ?? coords.length - 1);
  const i1 = Math.max(wpIndices[startIdx] ?? 0, wpIndices[endIdx] ?? coords.length - 1);
  const sliced = coords.slice(i0, i1 + 1);
  if (sliced.length < 2) return null;
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: sliced },
    properties: {},
  };
}

// Segment indicator colors (match map waypoint markers when segment is active)
export const SEGMENT_COLORS = [
  '#DD4130', /* red */
  '#2563eb', /* blue */
  '#059669', /* emerald */
  '#7c3aed', /* violet */
  '#ea580c', /* orange */
  '#0d9488', /* teal */
  '#dc2626', /* red-6 */
  '#ca8a04', /* yellow/amber */
  '#db2777', /* pink */
  '#0891b2', /* cyan */
];

export function getNextSegmentColorIndex(segments = []) {
  const usedColorIndices = new Set((segments || []).map((seg) => (seg?.colorIndex ?? 0)));
  for (let i = 0; i < SEGMENT_COLORS.length; i += 1) {
    if (!usedColorIndices.has(i)) return i;
  }
  return 0;
}

export function parseLinearMetersPerUnitFromWkt(wkt) {
  if (!wkt || typeof wkt !== 'string') return null;
  const re = /UNIT\["[^"]+"\s*,\s*([0-9.+\-eE]+)\s*\]/g;
  let match;
  let last = null;
  while ((match = re.exec(wkt)) !== null) {
    const v = Number(match[1]);
    if (Number.isFinite(v) && v > 0) last = v;
  }
  return last;
}

export function parseUnitsTokenFromProj4(proj4String) {
  if (!proj4String || typeof proj4String !== 'string') return '';
  const m = proj4String.match(/\+units=([^\s]+)/i);
  return (m?.[1] || '').toLowerCase();
}

export function getProjectedUnitsPerFoot({ wkt, proj4String }) {
  const metersPerUnitFromWkt = parseLinearMetersPerUnitFromWkt(wkt);
  if (Number.isFinite(metersPerUnitFromWkt) && metersPerUnitFromWkt > 0) {
    return 0.3048 / metersPerUnitFromWkt;
  }
  const token = parseUnitsTokenFromProj4(proj4String);
  if (token === 'us-ft' || token === 'ft') return 1;
  if (token === 'm') return 0.3048;
  return 0.3048;
}

export function normalizeVector2(x, y) {
  const len = Math.hypot(x, y);
  if (!Number.isFinite(len) || len <= 1e-9) return [0, 0];
  return [x / len, y / len];
}

export function offsetPolylineXY(polylineXY, signedOffsetUnits) {
  if (!Array.isArray(polylineXY) || polylineXY.length < 2) return [];
  const pts = polylineXY.map(([x, y]) => [Number(x), Number(y)]);
  if (pts.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) return [];
  const n = pts.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const p = pts[i];
    let tx = 0;
    let ty = 0;
    if (i === 0) {
      tx = pts[1][0] - p[0];
      ty = pts[1][1] - p[1];
    } else if (i === n - 1) {
      tx = p[0] - pts[n - 2][0];
      ty = p[1] - pts[n - 2][1];
    } else {
      const [p0, p1, p2] = [pts[i - 1], p, pts[i + 1]];
      const [aX, aY] = normalizeVector2(p1[0] - p0[0], p1[1] - p0[1]);
      const [bX, bY] = normalizeVector2(p2[0] - p1[0], p2[1] - p1[1]);
      tx = aX + bX;
      ty = aY + bY;
      if (Math.hypot(tx, ty) <= 1e-6) {
        tx = bX || aX;
        ty = bY || aY;
      }
    }
    const [nx, ny] = normalizeVector2(-ty, tx); // left-hand normal
    out.push([p[0] + (signedOffsetUnits * nx), p[1] + (signedOffsetUnits * ny)]);
  }
  return out;
}

export function clampNumber(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

export function projectLatLngToLocalMeters(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const origin = points[0];
  const lat0Rad = (origin.lat * Math.PI) / 180;
  const cosLat = Math.max(0.2, Math.cos(lat0Rad));
  return points.map((p) => ({
    x: (p.lng - origin.lng) * 111320 * cosLat,
    y: (p.lat - origin.lat) * 110540,
  }));
}

export function getDistanceMetersXY(a, b) {
  return Math.hypot((b.x - a.x), (b.y - a.y));
}

export function computeCurvatureScores(projectedPoints) {
  const n = Array.isArray(projectedPoints) ? projectedPoints.length : 0;
  const scores = new Array(n).fill(0);
  if (n < 3) return scores;

  for (let i = 1; i < n - 1; i += 1) {
    const a = projectedPoints[i - 1];
    const b = projectedPoints[i];
    const c = projectedPoints[i + 1];
    const v1x = b.x - a.x;
    const v1y = b.y - a.y;
    const v2x = c.x - b.x;
    const v2y = c.y - b.y;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 <= 1e-3 || len2 <= 1e-3) continue;
    const dot = clampNumber(((v1x * v2x) + (v1y * v2y)) / (len1 * len2), -1, 1);
    const angle = Math.acos(dot); // 0..PI
    const normalized = clampNumber(angle / Math.PI, 0, 1);
    scores[i] = normalized;
  }

  const smoothed = new Array(n).fill(0);
  for (let i = 0; i < n; i += 1) {
    const prev = scores[Math.max(0, i - 1)] ?? 0;
    const curr = scores[i] ?? 0;
    const next = scores[Math.min(n - 1, i + 1)] ?? 0;
    smoothed[i] = clampNumber((prev * 0.25) + (curr * 0.5) + (next * 0.25), 0, 1);
  }
  return smoothed;
}

export function sampleLaneWaypoints(latLngPoints) {
  const points = (Array.isArray(latLngPoints) ? latLngPoints : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (points.length < 2) return points;
  const projected = projectLatLngToLocalMeters(points);
  if (projected.length < 2) return points;

  const minSpacingMeters = 10 * 0.3048;   // 10 ft
  const maxSpacingMeters = 200 * 0.3048;  // 200 ft
  const curvature = computeCurvatureScores(projected);
  const cumulativeMeters = new Array(projected.length).fill(0);
  for (let i = 1; i < projected.length; i += 1) {
    cumulativeMeters[i] = cumulativeMeters[i - 1] + getDistanceMetersXY(projected[i - 1], projected[i]);
  }

  const chosenIndices = [0];
  let lastChosen = 0;
  let localCurvePeak = 0;

  for (let i = 1; i < projected.length - 1; i += 1) {
    localCurvePeak = Math.max(localCurvePeak, curvature[i] ?? 0);
    const distanceFromLast = cumulativeMeters[i] - cumulativeMeters[lastChosen];

    const curveFactor = clampNumber(localCurvePeak * 1.35, 0, 1);
    const desiredSpacingMeters = maxSpacingMeters - ((maxSpacingMeters - minSpacingMeters) * curveFactor);

    if (distanceFromLast >= desiredSpacingMeters) {
      chosenIndices.push(i);
      lastChosen = i;
      localCurvePeak = 0;
    }
  }

  if (chosenIndices[chosenIndices.length - 1] !== projected.length - 1) {
    chosenIndices.push(projected.length - 1);
  }

  const deduped = [];
  chosenIndices.forEach((idx) => {
    if (deduped.length === 0 || deduped[deduped.length - 1] !== idx) deduped.push(idx);
  });

  let sampled = deduped.map((idx) => ({ lat: points[idx].lat, lng: points[idx].lng }));
  const maxLaneWaypoints = 48;
  if (sampled.length > maxLaneWaypoints) {
    const n = sampled.length;
    const capped = [];
    for (let k = 0; k < maxLaneWaypoints; k += 1) {
      const i = Math.round((k / (maxLaneWaypoints - 1)) * (n - 1));
      capped.push(sampled[i]);
    }
    sampled = capped.filter((p, j, arr) => j === 0 || p.lat !== arr[j - 1].lat || p.lng !== arr[j - 1].lng);
  }
  return sampled;
}

// Copy-node sampling parameters. A node is placed every COPY_NODE_ANGLE_STEP_DEG
// of *accumulated* bend (dense on curves, floored at COPY_NODE_MIN_SPACING_FT),
// or every COPY_NODE_MAX_SPACING_FT along straight runs. Accumulating turning —
// rather than keying off per-vertex curvature like sampleLaneWaypoints — is what
// densifies smooth, densely-sampled road bends (each vertex turns only a little,
// but the bend's total turning adds up) instead of cutting the corner.
const COPY_NODE_MIN_SPACING_FT = 10;
const COPY_NODE_MAX_SPACING_FT = 200;
const COPY_NODE_ANGLE_STEP_DEG = 3;
const COPY_NODE_MAX_COUNT = 1000;

// Unsigned turn angle (radians, 0..PI) at b, between segments a->b and b->c.
function turnAngleXY(a, b, c) {
  const v1x = b.x - a.x;
  const v1y = b.y - a.y;
  const v2x = c.x - b.x;
  const v2y = c.y - b.y;
  const len1 = Math.hypot(v1x, v1y);
  const len2 = Math.hypot(v2x, v2y);
  if (len1 <= 1e-6 || len2 <= 1e-6) return 0;
  const dot = clampNumber((v1x * v2x + v1y * v2y) / (len1 * len2), -1, 1);
  return Math.acos(dot);
}

/**
 * sampleCurveNodes — turning-aware node sampling for a copied lane.
 *
 * Walks the (dense) geometry and keeps a vertex whenever the bend accumulated
 * since the last kept node reaches COPY_NODE_ANGLE_STEP_DEG (but no closer than
 * COPY_NODE_MIN_SPACING_FT), or whenever COPY_NODE_MAX_SPACING_FT of straight
 * run has passed. Result: ~10 ft nodes through curves, wide spacing on straights,
 * always including the endpoints. Every node lies on an input vertex (on-curve),
 * so the centripetal spline through them tracks the source without stiffness.
 *
 * @param {Array<{lat:number,lng:number}>} latLngPoints
 * @returns {Array<{lat:number,lng:number}>}
 */
export function sampleCurveNodes(latLngPoints) {
  const points = (Array.isArray(latLngPoints) ? latLngPoints : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (points.length < 2) return points;
  const proj = projectLatLngToLocalMeters(points);
  if (proj.length < 2) return points;

  const minM = COPY_NODE_MIN_SPACING_FT * 0.3048;
  const maxM = COPY_NODE_MAX_SPACING_FT * 0.3048;
  const angleStep = (COPY_NODE_ANGLE_STEP_DEG * Math.PI) / 180;

  const chosen = [0];
  let distSince = 0;
  let turnSince = 0;
  for (let i = 1; i < proj.length - 1; i += 1) {
    distSince += getDistanceMetersXY(proj[i - 1], proj[i]);
    turnSince += turnAngleXY(proj[i - 1], proj[i], proj[i + 1]);
    const hitStraight = distSince >= maxM;
    const hitCurve = turnSince >= angleStep && distSince >= minM;
    if (hitStraight || hitCurve) {
      chosen.push(i);
      distSince = 0;
      turnSince = 0;
    }
  }
  if (chosen[chosen.length - 1] !== proj.length - 1) chosen.push(proj.length - 1);

  let nodes = chosen.map((idx) => ({ lat: points[idx].lat, lng: points[idx].lng }));
  if (nodes.length > COPY_NODE_MAX_COUNT) {
    // Safety valve: evenly subsample to the cap, keeping both endpoints.
    const n = nodes.length;
    const capped = [];
    for (let k = 0; k < COPY_NODE_MAX_COUNT; k += 1) {
      capped.push(nodes[Math.round((k / (COPY_NODE_MAX_COUNT - 1)) * (n - 1))]);
    }
    nodes = capped.filter(
      (p, j, arr) => j === 0 || p.lat !== arr[j - 1].lat || p.lng !== arr[j - 1].lng,
    );
  }
  return nodes;
}

/**
 * generatedLaneGeometryCoords — the geometry to STORE in a generated lane's
 * pathData so it equals the line the map renderer actually draws.
 *
 * A generated/active lane is drawn twice: a non-clickable persistent overlay from
 * `pathData`, and the clickable active line. The active line is the centripetal
 * spline through the lane's nodes (for >2 nodes), or the stored pathData verbatim
 * via `useBaseline` (for exactly 2 endpoint nodes). If pathData doesn't match the
 * spline, the two renders bow apart between nodes — a "double line" that only
 * reconciles after an edit fires onPathChange. Storing the spline up front makes
 * them coincide immediately.
 *
 * @param {Array<{lat:number,lng:number}>} nodes - the lane's waypoints/nodes
 * @param {Array<{lat:number,lng:number}>} fallbackLatLng - exact geometry used when
 *   there are <=2 nodes (the renderer draws pathData verbatim via useBaseline)
 * @returns {Array<[number,number]>} GeoJSON [lng,lat] coordinates
 */
export function generatedLaneGeometryCoords(nodes, fallbackLatLng) {
  const cleanNodes = (Array.isArray(nodes) ? nodes : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  const geom =
    cleanNodes.length > 2
      ? catmullRomSpline(cleanNodes, SPLINE_SAMPLES_EDIT)
      : (Array.isArray(fallbackLatLng) ? fallbackLatLng : []);
  return geom
    .map((p) => {
      const lng = Array.isArray(p) ? Number(p[0]) : Number(p?.lng);
      const lat = Array.isArray(p) ? Number(p[1]) : Number(p?.lat);
      return [lng, lat];
    })
    .filter(([lng, lat]) => Number.isFinite(lat) && Number.isFinite(lng));
}

/**
 * resolveCopiedLaneNodes — editable nodes for a TRUE copy (offset === 0).
 *
 * The copy must be reshapeable in edit mode (nodes along the curve) AND track the
 * source. Hybrid by source type:
 *  - Lane source with >= 2 usable nodes -> reuse the lane's OWN nodes verbatim
 *    (reversed when directionMode === 'reverse'). Lanes render via the same
 *    centripetal spline, so the copy is visually identical and editable with the
 *    same nodes.
 *  - Path source (or a lane lacking nodes) -> turning-aware sample of the copied
 *    GEOMETRY into on-curve nodes via sampleCurveNodes (~10 ft through bends).
 *    NOT the path's sparse Google click-points (those don't lie on the snapped
 *    route → an offset/chord line). Centripetal spline through on-curve nodes
 *    tracks the route and is editable.
 *
 * @param {object} params
 * @param {boolean} params.isLaneSource - true when copying a lane, false for a path
 * @param {Array<{lat:number,lng:number}>} params.sourceWaypoints - the source's nodes
 * @param {Array<{lat:number,lng:number}>} params.geometryLatLng - the copied lane
 *   geometry, already ordered (reversed upstream when 'reverse')
 * @param {boolean} [params.reverse] - true when directionMode === 'reverse'
 * @returns {Array<{lat:number,lng:number}>} editable nodes for the new lane
 */
export function resolveCopiedLaneNodes({
  isLaneSource,
  sourceWaypoints,
  geometryLatLng,
  reverse = false,
}) {
  if (isLaneSource) {
    const cleaned = (Array.isArray(sourceWaypoints) ? sourceWaypoints : [])
      .map((wp) => ({ lat: Number(wp?.lat), lng: Number(wp?.lng) }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (cleaned.length >= 2) return reverse ? cleaned.reverse() : cleaned;
  }
  return sampleCurveNodes(geometryLatLng);
}

export function getPathDisplayName(path, idx = 0) {
  const label = String(path?.label ?? '').trim();
  if (label) return label;
  return `Route${idx + 1}`;
}

export function getLaneDisplayName(lane, idx = 0) {
  const label = String(lane?.label ?? '').trim();
  if (label) return label;
  return `Lane ${idx + 1}`;
}

export function getSegmentDisplayName(seg, idx) {
  // Lanes carry a pathId pointing at their parent path; paths don't.
  // That's the only reliable shape difference, so anything without a
  // pathId (and not explicitly tagged 'lane') is treated as a path.
  if (seg?.pathId != null || seg?.type === 'lane') {
    return getLaneDisplayName(seg, idx);
  }
  return getPathDisplayName(seg, idx);
}

export const EMPTY_WAYPOINTS = [];

export const METERS_TO_FEET = 3.28084;

/** Haversine distance between two points in meters */
export function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Normalize path point to { lat, lng } */
function pathPointToLatLng(p) {
  if (!p) return null;
  if (Array.isArray(p)) return { lat: p[1], lng: p[0] };
  const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
  const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
  return { lat, lng };
}

/**
 * Resample a path to coordinates every N feet of TOTAL chainage along the path.
 * Distance accumulates continuously across vertices — a leftover from one
 * segment carries into the next, so spacing stays exactly intervalFeet
 * everywhere regardless of where the input vertices fall. Input vertices shape
 * the line but are NOT emitted (unless one lands on the grid). Output is the
 * exact start, then every intervalFeet of chainage. A sub-interval leftover at
 * the end is DROPPED (the path end is emitted only when the total length is an
 * exact multiple of intervalFeet), so every emitted gap is exactly
 * intervalFeet.
 * @param path - Array of points: { lat, lng } or [lng, lat]
 * @param intervalFeet - Spacing in feet (e.g. 10)
 * @returns Array of { lat, lng }
 */
export function getPathCoordinatesEveryFeet(path, intervalFeet = 10) {
  const pts = path.map(pathPointToLatLng).filter((p) => p != null && p.lat != null && p.lng != null);
  if (pts.length < 2) return pts.length ? [pts[0]] : [];
  const result = [{ lat: pts[0].lat, lng: pts[0].lng }];
  let traveledFt = 0; // chainage at the start of the current segment
  let nextFt = intervalFeet; // chainage of the next grid point to emit
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const segLenFt = haversineMeters(a.lat, a.lng, b.lat, b.lng) * METERS_TO_FEET;
    if (!(segLenFt > 0)) continue; // zero-length or invalid segment
    const segEndFt = traveledFt + segLenFt;
    while (nextFt < segEndFt - 1e-6) {
      const t = (nextFt - traveledFt) / segLenFt;
      result.push({
        lat: a.lat + t * (b.lat - a.lat),
        lng: a.lng + t * (b.lng - a.lng),
      });
      nextFt += intervalFeet;
    }
    traveledFt = segEndFt;
  }
  // After the loop, traveledFt is the total length and nextFt is the first
  // unemitted grid mark. The endpoint is kept only when it lands on the grid
  // (nextFt ≈ total); the loop's epsilon guarantees it was not also emitted as
  // an interpolated point, so the end can never appear twice.
  if (nextFt <= traveledFt + 1e-6) {
    const last = pts[pts.length - 1];
    result.push({ lat: last.lat, lng: last.lng });
  }
  return result;
}

/**
 * The dense lat/lng polyline a lane RENDERS as — for save/export use.
 * Mirrors the renderer's decision (see generatedLaneGeometryCoords):
 *  - > 2 waypoints → the centripetal spline through them (waypoints are
 *    authoritative),
 *  - ≤ 2 waypoints → the exact pathData geometry (useBaseline draws pathData
 *    verbatim for a generated lane not yet reshaped),
 *  - 2 waypoints with no pathData → the straight pair itself.
 * Exporting raw waypoints instead of this yields chord polylines that render
 * as broken/faceted curves in CAD.
 * @returns Array of { lat, lng } (possibly empty)
 */
export function laneDenseGeometryLatLng(lane) {
  const fallback = Array.isArray(lane?.pathData?.geometry?.coordinates)
    ? lane.pathData.geometry.coordinates
    : [];
  const dense = generatedLaneGeometryCoords(lane?.waypoints, fallback);
  if (dense.length >= 2) return dense.map(([lng, lat]) => ({ lat, lng }));
  return (Array.isArray(lane?.waypoints) ? lane.waypoints : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
}
